package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsCenterStageAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apPurple: N,
    override val apYellow: N,
    override val apNavigation: N,
    override val apBackstage: N,
    override val apBackdrop: N,
    override val tpBackstage: N,
    override val tpBackdrop: N,
    override val tpMosaic: N,
    override val tpSet: N,
    override val epDrone: N,
    override val epHang: N,
    override val epPark: N,
) : MetricsAPI<N>, MetricsCenterStage<N> {
    constructor(team1: MetricsCenterStage<N>, team2: MetricsCenterStage<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apPurple = team1.apPurple + team2.apPurple,
        apYellow = team1.apYellow + team2.apYellow,
        apNavigation = team1.apNavigation + team2.apNavigation,
        apBackstage = team1.apBackstage + team2.apBackstage,
        apBackdrop = team1.apBackdrop + team2.apBackdrop,
        tpBackstage = team1.tpBackstage + team2.tpBackstage,
        tpBackdrop = team1.tpBackdrop + team2.tpBackdrop,
        tpMosaic = team1.tpMosaic + team2.tpMosaic,
        tpSet = team1.tpSet + team2.tpSet,
        epDrone = team1.epDrone + team2.epDrone,
        epHang = team1.epHang + team2.epHang,
        epPark = team1.epPark + team2.epPark,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsCenterStage<*>, team2: MetricsCenterStage<*>) = MetricsCenterStageAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apPurple = team1.apPurple.convert<N>() + team2.apPurple.convert<N>(),
            apYellow = team1.apYellow.convert<N>() + team2.apYellow.convert<N>(),
            apNavigation = team1.apNavigation.convert<N>() + team2.apNavigation.convert<N>(),
            apBackstage = team1.apBackstage.convert<N>() + team2.apBackstage.convert<N>(),
            apBackdrop = team1.apBackdrop.convert<N>() + team2.apBackdrop.convert<N>(),
            tpBackstage = team1.tpBackstage.convert<N>() + team2.tpBackstage.convert<N>(),
            tpBackdrop = team1.tpBackdrop.convert<N>() + team2.tpBackdrop.convert<N>(),
            tpMosaic = team1.tpMosaic.convert<N>() + team2.tpMosaic.convert<N>(),
            tpSet = team1.tpSet.convert<N>() + team2.tpSet.convert<N>(),
            epDrone = team1.epDrone.convert<N>() + team2.epDrone.convert<N>(),
            epHang = team1.epHang.convert<N>() + team2.epHang.convert<N>(),
            epPark = team1.epPark.convert<N>() + team2.epPark.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsCenterStage<*>.convert() = serialize { convert<N>() }

fun <N : Number> MetricsCenterStage<N>.serialize() = MetricsCenterStageAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apPurple = apPurple,
    apYellow = apYellow,
    apNavigation = apNavigation,
    apBackstage = apBackstage,
    apBackdrop = apBackdrop,
    tpBackstage = tpBackstage,
    tpBackdrop = tpBackdrop,
    tpMosaic = tpMosaic,
    tpSet = tpSet,
    epDrone = epDrone,
    epHang = epHang,
    epPark = epPark,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsCenterStage<T>.serialize(serializer: T.() -> R) = MetricsCenterStageAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apPurple = apPurple.serializer(),
    apYellow = apYellow.serializer(),
    apNavigation = apNavigation.serializer(),
    apBackstage = apBackstage.serializer(),
    apBackdrop = apBackdrop.serializer(),
    tpBackstage = tpBackstage.serializer(),
    tpBackdrop = tpBackdrop.serializer(),
    tpMosaic = tpMosaic.serializer(),
    tpSet = tpSet.serializer(),
    epDrone = epDrone.serializer(),
    epHang = epHang.serializer(),
    epPark = epPark.serializer(),
)

@JvmName("serializeDouble")
fun MetricsCenterStage<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsCenterStage<Number>.serialize() = serialize { toShort() }

fun MetricsCenterStage<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apPurple: $apPurple
        apYellow: $apYellow
        apNavigation: $apNavigation
        apBackstage: $apBackstage
        apBackdrop: $apBackdrop
        tpBackstage: $tpBackstage
        tpBackdrop: $tpBackdrop
        tpMosaic: $tpMosaic
        tpSet: $tpSet
        epDrone: $epDrone
        epHang: $epHang
        epPark: $epPark
    """.trimIndent()

object MetricsCenterStageMeta : MetricsMeta<MetricsCenterStage<*>> {
    override val names = linkedMapOf(
        MetricsCenterStage<*>::total to "Total",
        MetricsCenterStage<*>::npTotal to "NP Total",
        MetricsCenterStage<*>::auto to "Auto",
        MetricsCenterStage<*>::teleop to "Teleop",
        MetricsCenterStage<*>::endgame to "Endgame",
        MetricsCenterStage<*>::penalty to "Penalty",
        MetricsCenterStage<*>::apPurple to "Purple",
        MetricsCenterStage<*>::apYellow to "Yellow",
        MetricsCenterStage<*>::apNavigation to "Navigation",
        MetricsCenterStage<*>::apBackstage to "Backstage",
        MetricsCenterStage<*>::apBackdrop to "Backdrop",
        MetricsCenterStage<*>::tpBackstage to "Backstage",
        MetricsCenterStage<*>::tpBackdrop to "Backdrop",
        MetricsCenterStage<*>::tpMosaic to "Mosaic",
        MetricsCenterStage<*>::tpSet to "Set",
        MetricsCenterStage<*>::epDrone to "Drone",
        MetricsCenterStage<*>::epHang to "Hang",
        MetricsCenterStage<*>::epPark to "Park",
    )
    
    override val periods = linkedMapOf(
        MetricsCenterStage<*>::total to ALL,
        MetricsCenterStage<*>::npTotal to ALL,
        MetricsCenterStage<*>::auto to AUTO,
        MetricsCenterStage<*>::teleop to TELEOP,
        MetricsCenterStage<*>::endgame to ENDGAME,
        MetricsCenterStage<*>::penalty to ALL,
        MetricsCenterStage<*>::apPurple to AUTO,
        MetricsCenterStage<*>::apYellow to AUTO,
        MetricsCenterStage<*>::apNavigation to AUTO,
        MetricsCenterStage<*>::apBackstage to AUTO,
        MetricsCenterStage<*>::apBackdrop to AUTO,
        MetricsCenterStage<*>::tpBackstage to TELEOP,
        MetricsCenterStage<*>::tpBackdrop to TELEOP,
        MetricsCenterStage<*>::tpMosaic to TELEOP,
        MetricsCenterStage<*>::tpSet to TELEOP,
        MetricsCenterStage<*>::epDrone to ENDGAME,
        MetricsCenterStage<*>::epHang to ENDGAME,
        MetricsCenterStage<*>::epPark to ENDGAME,
    )
}
